import { Controller } from "@hotwired/stimulus";

function loadStripeJs() {
  if (typeof globalThis.Stripe === "function") {
    return Promise.resolve();
  }

  if (globalThis.__donorstackStripeLoadPromise) {
    return globalThis.__donorstackStripeLoadPromise;
  }

  globalThis.__donorstackStripeLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Stripe.js failed to load"));
    document.head.appendChild(script);
  });

  return globalThis.__donorstackStripeLoadPromise;
}

export default class StripeCardVerificationController extends Controller {
  static values = {
    publishableKey: String,
    setupIntentUrl: String,
    completeUrl: String,
  };

  static targets = ["mount", "errors", "submit"];

  connect() {
    this.boot().catch((e) => {
      console.error(e);
      this.showError(e?.message || "Could not load payment form.");
    });
  }

  async boot() {
    const pk = (this.publishableKeyValue || "").trim();
    if (!pk) {
      this.showError(
        "Stripe is not configured. Set stripe environment variables.",
      );
      return;
    }

    try {
      await loadStripeJs();
    } catch {
      this.showError(
        "Could not load Stripe.js. Check your network connection, firewall, or ad blocker.",
      );
      return;
    }

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!csrf) {
      this.showError("Missing security token. Try a full page reload.");
      return;
    }

    this.stripe = globalThis.Stripe(pk);

    const params = new URLSearchParams(globalThis.location.search);
    const clientSecretFromRedirect = params.get("setup_intent_client_secret");
    if (clientSecretFromRedirect) {
      await this.finishAfterRedirect(clientSecretFromRedirect, csrf);
      return;
    }

    const intentRes = await fetch(this.setupIntentUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    });

    const rawIntent = await intentRes.text();
    let intentPayload = {};
    try {
      intentPayload = rawIntent ? JSON.parse(rawIntent) : {};
    } catch {
      this.showError(
        intentRes.ok
          ? "Invalid response from server."
          : `Could not start card verification (${intentRes.status}). ${rawIntent.slice(0, 200)}`,
      );
      return;
    }

    if (!intentRes.ok) {
      this.showError(
        intentPayload.error || "Could not start card verification.",
      );
      return;
    }

    const { client_secret: clientSecret } = intentPayload;
    if (!clientSecret) {
      this.showError("Invalid response from server.");
      return;
    }

    try {
      this.elements = this.stripe.elements({
        clientSecret,
        appearance: { theme: "stripe" },
      });
      this.paymentElement = this.elements.create("payment");
      this.paymentElement.mount(this.mountTarget);
    } catch (e) {
      console.error(e);
      this.showError(e?.message || "Payment form failed to load.");
      return;
    }

    this.submitTarget.disabled = false;
    this.submitTarget.addEventListener("click", () => this.submit(csrf));
  }

  async finishAfterRedirect(clientSecret, csrf) {
    const { error, setupIntent } =
      await this.stripe.retrieveSetupIntent(clientSecret);
    if (error) {
      this.showError(error.message);
      return;
    }
    if (setupIntent.status !== "succeeded") {
      this.showError("Card verification is not complete.");
      return;
    }

    const ok = await this.postComplete(setupIntent.id, csrf);
    if (ok) {
      const cleanUrl = globalThis.location.pathname + globalThis.location.hash;
      globalThis.history.replaceState(null, "", cleanUrl);
    }
  }

  async postComplete(setupIntentId, csrf) {
    const completeRes = await fetch(this.completeUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: JSON.stringify({ setup_intent_id: setupIntentId }),
    });

    const completePayload = await completeRes.json().catch(() => ({}));
    if (!completeRes.ok) {
      this.showError(
        completePayload.error || "Could not complete verification.",
      );
      return false;
    }

    if (completePayload.redirect_url) {
      globalThis.location.href = completePayload.redirect_url;
    } else {
      globalThis.location.reload();
    }
    return true;
  }

  async submit(csrf) {
    this.hideError();
    this.submitTarget.disabled = true;

    const returnUrl = globalThis.location.href.split("#")[0];

    const { error, setupIntent } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: "if_required",
    });

    if (error) {
      this.showError(error.message);
      this.submitTarget.disabled = false;
      return;
    }

    const ok = await this.postComplete(setupIntent.id, csrf);
    if (!ok) {
      this.submitTarget.disabled = false;
    }
  }

  showError(message) {
    this.errorsTarget.textContent = message;
    this.errorsTarget.style.display = "block";
  }

  hideError() {
    this.errorsTarget.textContent = "";
    this.errorsTarget.style.display = "none";
  }
}
